import { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import {
  ActionData,
  SlashMenu as AssemblySlashMenu,
  Toolbar,
  ToolbarProvider,
} from 'copilot-design-system';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useEffect } from 'react';

import { EDITOR_ACTIONS_CONFIG } from 'src/components/tiptap/constants';
import { SlashCommand } from 'src/components/tiptap/custom-extensions/SlashCommand';
import { SlashMenuRenderer } from 'src/components/tiptap/custom-extensions/SlashMenuRenderer';
import { HighlightToolbar } from 'src/components/tiptap/highlight-menu/HighlightToolbar';
import { RichTextStyles } from 'src/components/tiptap/RichTextStyles';
import { SlashMenu } from 'src/components/tiptap/slash-menu/SlashMenu';
import { getSlashMenuSuggestionItems } from 'src/features/notes/NoteModal/utils';

// Supported toolbar actions
const BILLING_EDITOR_TOOLBAR_ACTIONS = [
  'heading2',
  'heading3',
  'bold',
  'italic',
  'underline',
  'bulletList',
  'orderedList',
];

// Supported slash menu actions
const BILLING_EDITOR_SLASH_MENU_ACTIONS = [
  'heading2',
  'heading3',
  'text',
  'bulletList',
  'orderedList',
];
interface Props {
  description: string;
  onContentChange: (editor: Editor) => void;
  onUpdateTipTapEditor?: (edited: Editor | null) => void;
  className?: string;
}

export const BillingRichTextEditor: React.FC<Props> = ({
  description,
  onContentChange,
  onUpdateTipTapEditor,
  className,
}) => {
  const EnableNewEditorFormatter = useFeatureFlagEnabled(
    'new-editor-formatter',
  );
  const slashMenuRangeRef = React.useRef<{ from: number; to: number }>({
    from: 0,
    to: 0,
  });

  const tiptapEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Placeholder.configure({
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:float-left before:text-[#9B9FA3] before:h-0 before:pointer-events-none',
        placeholder: '',
      }),
      Underline,
      Link,
      ListKeymap,
      SlashCommand.configure({
        suggestion: {
          items: getSlashMenuSuggestionItems,
          render: () =>
            SlashMenuRenderer({
              Component: EnableNewEditorFormatter
                ? ({ range }) => {
                    // the action handler need to know in which position
                    // the typing head is so that when the action is triggered
                    // from the slashmenu, it delete the "/" and handle
                    // the side-effects.
                    slashMenuRangeRef.current = range;
                    return (
                      <AssemblySlashMenu
                        overrideActions={BILLING_EDITOR_SLASH_MENU_ACTIONS}
                      />
                    );
                  }
                : SlashMenu,
              tippyProps: {
                placement: 'bottom-start',
              },
            }),
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: clsx(
          className,
          'flex flex-col gap-1 mt-1 min-h-[64px] relative',
        ),
      },
    },
    content: description,
    onCreate: ({ editor }) => onContentChange(editor),
    onUpdate: ({ editor }) => onContentChange(editor),
  });

  useEffect(() => {
    onUpdateTipTapEditor?.(tiptapEditor);
  }, [onUpdateTipTapEditor, tiptapEditor]);

  const getActionState = React.useCallback(
    (actionId: string, _?: Record<string, unknown>) => {
      // if the action is a heading action, we need to pass
      // the level to the isActive function
      if (actionId.startsWith('heading')) {
        return {
          isActive:
            tiptapEditor?.isActive('heading', {
              level: parseInt(actionId.split('heading')[1]),
            }) ?? false,
          isDisabled: false,
        };
      }
      // if the action is a text action, we need to pass the text to the isActive function
      if (actionId === 'text') {
        return {
          isActive: tiptapEditor?.isActive('paragraph') ?? false,
          isDisabled: false,
        };
      }

      return {
        isActive: tiptapEditor?.isActive(actionId) ?? false,
        isDisabled: false,
      };
    },
    [tiptapEditor],
  );

  const handleAction = React.useCallback(
    (actionData: ActionData) => {
      if (!tiptapEditor) return;
      switch (actionData.action) {
        case 'bold':
          tiptapEditor.chain().focus().toggleBold().run();
          break;
        case 'italic':
          tiptapEditor.chain().focus().toggleItalic().run();
          break;
        case 'underline':
          tiptapEditor.chain().focus().toggleUnderline().run();
          break;
        case 'paragraph':
          tiptapEditor.chain().focus().setParagraph().run();
          break;
        case 'heading1':
          tiptapEditor
            .chain()
            .focus()
            .deleteRange(slashMenuRangeRef.current)
            .setNode('heading', { level: 1 })
            .run();
          break;
        case 'heading2':
          tiptapEditor
            .chain()
            .focus()
            .deleteRange(slashMenuRangeRef.current)
            .setNode('heading', { level: 2 })
            .run();
          break;
        case 'heading3':
          tiptapEditor
            .chain()
            .focus()
            .deleteRange(slashMenuRangeRef.current)
            .setNode('heading', { level: 3 })
            .run();
          break;
        default:
          tiptapEditor
            .chain()
            .focus()
            .deleteRange(slashMenuRangeRef.current)
            .run();
          break;
        case 'bulletList':
          tiptapEditor
            .chain()
            .focus()
            .deleteRange(slashMenuRangeRef.current)
            .toggleBulletList()
            .run();
          break;
        case 'orderedList':
          tiptapEditor
            .chain()
            .focus()
            .deleteRange(slashMenuRangeRef.current)
            .toggleOrderedList()
            .run();
          break;
      }
    },
    [tiptapEditor],
  );

  return (
    <ToolbarProvider
      config={EDITOR_ACTIONS_CONFIG}
      onAction={handleAction}
      getActionState={getActionState}
    >
      <div
        className={clsx(
          'relative box-border border border-solid rounded max-h-64 overflow-auto [&>div>div]:!z-modal',
          {
            'border-black-heading': tiptapEditor?.isFocused,
            'border-non-hover-border': !tiptapEditor?.isFocused,
          },
        )}
      >
        <HighlightToolbar
          tiptapEditor={tiptapEditor}
          enableNewEditorFormatter={EnableNewEditorFormatter}
        />
        {tiptapEditor && EnableNewEditorFormatter && (
          <Toolbar
            className="w-[-webkit-fill-available]"
            variant="sticky"
            overrideActions={BILLING_EDITOR_TOOLBAR_ACTIONS}
          />
        )}
        <div className="px-3.5 py-3">
          <RichTextStyles>
            <EditorContent editor={tiptapEditor} />
          </RichTextStyles>
        </div>
      </div>
    </ToolbarProvider>
  );
};
